import React, { useState, useEffect } from "react";
import { X, Bell, User, FileText, Paperclip } from "lucide-react";
import { httpClient } from "../../../../../HttpClient";
import UserSearchTagPicker from "../../../../../components/ui/UserSearchTagPicker";
import { uploadFileToBlob } from "../../../../../utils/uploadFileToBlob";

const NIVEAU_LABELS = {
  1: "Numérisation & Dépôt",
  2: "Pré-compta",
  3: "Révision",
};

// Fichiers joints (ticket de Morgane SALAÜN, 22/10/2026 : 4 Mo et un seul
// fichier obligeaient à multiplier les notifications dès plus de deux
// relevés). Même fonctionnement que la clôture d'étape (ClotureStageModal.js) :
// fichiers de 25 Mo, déposés directement du navigateur sur Vercel Blob
// (uploadFileToBlob, jeton /traitements-comptables/upload-token) — ils ne
// transitent plus par le serveur, d'où la fin de la limite de 4,5 Mo. Le
// backend les joint au mail jusqu'à 18 Mo au total, les suivants en lien.
// Plus de limite de nombre (ticket du 29/10/2026, la limite de 5 fichiers
// par notification gênait) — sauf le garde-fou technique du backend (100).
const TAILLE_MAX = 25 * 1024 * 1024;
const EXTENSIONS_FICHIERS = ".pdf,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic";
const UPLOAD_TOKEN_URL = "https://backend-myalfa.vercel.app/api/traitements-comptables/upload-token";

const tailleLisible = (o) =>
  o >= 1024 * 1024 ? `${(o / 1024 / 1024).toFixed(1).replace(".", ",")} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`;

// Modale de notification (cloche) : deux champs de destinataires distincts —
// "Principal" (To, une seule personne, obligatoire) et "En copie" (CC,
// plusieurs personnes, optionnel) — plutôt qu'une liste unique dans laquelle
// on désignerait après coup laquelle est principale (demande Jonathan,
// 22/09/2026). Par défaut : le principal est l'affecté de l'étape concernée
// (le vrai concerné), et les autres collaborateurs déjà affectés sur ce
// dossier (niveaux 1/2/3) sont pré-cochés en copie. Le tout reste librement
// modifiable. N'envoie qu'un email, ne modifie aucun état.
function NotifierPopover({ ticket, etape, users, utilisateur, onClose }) {
  const [primaryUserId, setPrimaryUserId] = useState(() => {
    const assigneeId = etape.assignee?.id;
    if (assigneeId) return assigneeId;
    const autreAssigne = (ticket.etapes || []).find((e) => e.assignee?.id);
    return autreAssigne?.assignee?.id ?? null;
  });
  const [ccUserIds, setCcUserIds] = useState(() =>
    Array.from(
      new Set(
        (ticket.etapes || [])
          .map((e) => e.assignee?.id)
          .filter((id) => id !== undefined && id !== null && id !== etape.assignee?.id)
      )
    )
  );
  const [comment, setComment] = useState("");
  const [fichiers, setFichiers] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null); // { n, total, pct }
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  // Une même personne ne doit jamais se retrouver à la fois en principal et
  // en copie.
  useEffect(() => {
    if (primaryUserId != null && ccUserIds.includes(primaryUserId)) {
      setCcUserIds((prev) => prev.filter((id) => id !== primaryUserId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryUserId]);

  const primaryUser = users.find((u) => u.id === primaryUserId);
  const primaryEmail = primaryUser?.email || null;
  const ccEmails = users
    .filter((u) => ccUserIds.includes(u.id))
    .map((u) => u.email)
    .filter(Boolean);
  const canSend = !!primaryEmail;

  // Plusieurs fichiers d'un coup possibles ; au-delà de TAILLE_MAX, les
  // fichiers trop volumineux sont écartés avec un message.
  const handleFileChange = (e) => {
    const choisis = Array.from(e.target.files || []);
    e.target.value = ""; // permet de resélectionner le même fichier après une erreur
    if (!choisis.length) return;

    const tropGros = choisis.filter((f) => f.size > TAILLE_MAX);
    const retenus = choisis.filter((f) => f.size <= TAILLE_MAX);
    setFileError(
      tropGros.length ? `${tropGros.map((f) => f.name).join(", ")} : trop volumineux (25 Mo maximum par fichier).` : null
    );
    setFichiers((prev) => [...prev, ...retenus]);
  };

  const removeFile = (index) => {
    setFichiers((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  };

  const handleSend = async () => {
    if (!canSend) return;

    setSending(true);
    setError(null);
    let phase = "fichiers";
    try {
      // 1. Dépôt direct des fichiers sur Vercel Blob (sans passer par le serveur).
      const fichiersEnvoyes = [];
      for (const [i, fichier] of fichiers.entries()) {
        setUploadProgress({ n: i + 1, total: fichiers.length, pct: 0 });
        const url = await uploadFileToBlob(fichier, UPLOAD_TOKEN_URL, ({ percentage }) =>
          setUploadProgress({ n: i + 1, total: fichiers.length, pct: Math.round(percentage) })
        );
        fichiersEnvoyes.push({ url, nom: fichier.name, taille: fichier.size });
      }
      setUploadProgress(null);

      // 2. Envoi de la notification : seules les URL des fichiers partent.
      phase = "notification";
      await httpClient.post("https://backend-myalfa.vercel.app/api/email-traitement-comptable-notification", {
        ticketId: ticket.id,
        clientNom: ticket.client?.nom || "",
        niveau: etape.niveau,
        message: comment.trim(),
        senderName: utilisateur ? `${utilisateur.name} ${utilisateur.lastName}` : "",
        recipientEmail: primaryEmail,
        ccEmails: JSON.stringify(ccEmails),
        // Pour marquer l'étape comme "notifiée" (bouton cloche en vert — voir
        // SuiviCell.js) : les ids, pas juste les emails, pour connecter
        // directement PieceComptableEtape.notifiedUsers côté backend.
        notifiedUserIds: JSON.stringify([primaryUserId, ...ccUserIds].filter(Boolean)),
        fichiers: JSON.stringify(fichiersEnvoyes),
      });
      setSent(true);
      setTimeout(onClose, 900);
    } catch (err) {
      console.error("Erreur envoi notification :", err);
      setError(
        err?.response?.data?.error ||
          (phase === "fichiers"
            ? "L'envoi d'un fichier a échoué. Vérifiez votre connexion et réessayez."
            : "Une erreur est survenue lors de l'envoi de la notification.")
      );
      setUploadProgress(null);
    } finally {
      setSending(false);
    }
  };

  const libelleEnvoi = sent
    ? "Envoyé !"
    : uploadProgress
    ? `Envoi du fichier ${uploadProgress.n}/${uploadProgress.total} (${uploadProgress.pct} %)…`
    : sending
    ? "Envoi..."
    : "Envoyer la notification";

  return (
    <div className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#5C1A30] flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 leading-tight">Notifier le collaborateur</h2>
              <p className="text-xs text-gray-400">
                {ticket.client?.nom} · Niveau {etape.niveau} — {NIVEAU_LABELS[etape.niveau] || ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="border border-gray-100 rounded-2xl p-4 space-y-4">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-bold text-[#5C1A30] mb-2">
                <User size={15} />
                Principal <span className="font-normal text-gray-400">(obligatoire*)</span>
              </p>
              <select
                value={primaryUserId ?? ""}
                onChange={(e) =>
                  setPrimaryUserId(e.target.value ? parseInt(e.target.value, 10) : null)
                }
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040] bg-white"
              >
                <option value="">Choisir un destinataire...</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} {u.lastName || ""}
                    {etape.assignee && u.id === etape.assignee.id
                      ? " (affecté à cette étape)"
                      : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p className="flex items-center gap-1.5 text-sm font-bold text-[#5C1A30] mb-2">
                <User size={15} />
                En copie <span className="font-normal text-gray-400">(optionnel)</span>
              </p>
              <UserSearchTagPicker
                allUsers={users.filter((u) => u.id !== primaryUserId)}
                selectedIds={ccUserIds}
                onAdd={(user) => setCcUserIds((prev) => [...prev, user.id])}
                onRemove={(id) =>
                  setCcUserIds((prev) => prev.filter((i) => i !== id))
                }
                emptyText="Aucune copie."
              />
            </div>
          </div>

          <div className="border border-gray-100 rounded-2xl p-4 space-y-3">
            <p className="flex items-center gap-1.5 text-sm font-bold text-[#5C1A30]">
              <FileText size={15} />
              Commentaire
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Message à envoyer au collaborateur..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040] resize-none"
            />
          </div>

          <div className="border border-gray-100 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-bold text-[#5C1A30] m-0">
                <Paperclip size={15} />
                Pièces jointes <span className="font-normal text-gray-400">(optionnel)</span>
              </p>
              {fichiers.length > 0 && (
                <span className="text-[11px] font-semibold tabular-nums text-gray-400">
                  {fichiers.length} fichier{fichiers.length > 1 ? "s" : ""}
                </span>
              )}
            </div>

            {fichiers.length > 0 && (
              <ul className="space-y-2 m-0 p-0 list-none">
                {fichiers.map((fichier, i) => (
                  <li key={`${fichier.name}-${i}`} className="flex items-center justify-between gap-3 bg-[#fff6ee] rounded-xl px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate m-0">{fichier.name}</p>
                      <p className="text-xs text-gray-400 m-0">{tailleLisible(fichier.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      disabled={sending}
                      title="Retirer le fichier"
                      aria-label={`Retirer ${fichier.name}`}
                      className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0 disabled:opacity-40"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <label
              className={`flex items-center gap-2 justify-center px-4 py-3 rounded-xl border border-dashed border-gray-300 text-sm text-gray-500 transition-colors ${
                sending ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-[#840040] hover:text-[#840040]"
              }`}
            >
              <Paperclip size={14} />
              {fichiers.length ? "Ajouter d'autres fichiers" : "Joindre des fichiers"}
              <input
                type="file"
                multiple
                accept={EXTENSIONS_FICHIERS}
                onChange={handleFileChange}
                disabled={sending}
                className="hidden"
              />
            </label>
            <p className="text-[11px] text-gray-400 m-0">
              Autant de fichiers que nécessaire, 25 Mo chacun (PDF, Word, Excel, images). Ils sont joints au mail ; au-delà
              de 18 Mo au total, les suivants y figurent en lien de téléchargement.
            </p>
            {fileError && <div className="text-sm text-red-600">{fileError}</div>}
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-[#840040] text-[#840040] text-sm font-semibold hover:bg-[#840040]/5 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={!canSend || sending}
            onClick={handleSend}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-colors ${
              !canSend || sending
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#5C1A30] text-white hover:bg-[#4a1526]"
            }`}
          >
            <Bell size={14} />
            {libelleEnvoi}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotifierPopover;
